import React, { FC, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import colors from 'src/styles/equalution/colors';
import { useAppSelector } from 'src/store/hooks';
import { formatIntervalDisplay, isUserFromAustralia } from 'src/lib/utils';
import { Plan } from 'src/pages/deprecated/payment/types';
import supplementsPackImage from 'src/assets/images/supplements-pack.png';

import { GIFT_CAMPAIGN, GiftBenefit } from './constants';
import { isPlanGiftEligible, joinPlanLabels } from './eligibility';

const GiftBenefitRow: FC<{ benefit: GiftBenefit }> = ({ benefit }) => (
  <Stack direction={'row'} alignItems={'flex-start'} spacing={'12px'}>
    <CheckIcon
      sx={{
        color: colors.SALAD,
        fontSize: 16,
        mt: '2px',
        flexShrink: 0,
      }}
    />
    <Stack spacing={'2px'} sx={{ flex: 1 }}>
      <Typography
        sx={{
          fontFamily: 'Lexend, sans-serif',
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          color: colors.OFF_BLACK,
          textAlign: 'left',
        }}>
        {benefit.title}
      </Typography>
      {benefit.subtitle && (
        <Typography
          sx={{
            fontFamily: 'Lexend, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: colors.BLUEBERRY,
            textAlign: 'left',
          }}>
          {benefit.subtitle}
        </Typography>
      )}
    </Stack>
  </Stack>
);

const GiftEligibilityCard: FC = () => {
  const { availablePlans, selectedPlanId } = useAppSelector(
    state => state.signUp
  );

  const isAU = isUserFromAustralia();

  const eligiblePlans = useMemo<Plan[]>(
    () => availablePlans.filter(isPlanGiftEligible),
    [availablePlans]
  );

  const selectedPlan = useMemo(
    () => availablePlans.find(p => p.id === selectedPlanId) ?? null,
    [availablePlans, selectedPlanId]
  );

  const eligiblePlanLabels = useMemo(
    () =>
      eligiblePlans.map(p =>
        formatIntervalDisplay(p.intervalCount, p.interval)
      ),
    [eligiblePlans]
  );

  if (!isAU) return null;
  if (eligiblePlans.length === 0) return null;

  const isUnlocked = !!selectedPlan && isPlanGiftEligible(selectedPlan);

  if (!isUnlocked) {
    const text = GIFT_CAMPAIGN.copy.nudgeTemplate.replace(
      '{{plans}}',
      joinPlanLabels(eligiblePlanLabels)
    );
    return (
      <Box
        mt={'16px'}
        mx={'8px'}
        px={'16px'}
        py={'12px'}
        bgcolor={colors.WHITE}
        borderRadius={'16px'}
        sx={{ border: `1px solid ${colors.SALAD}` }}>
        <Typography
          textAlign={'center'}
          sx={{
            fontFamily: 'Lexend, sans-serif',
            fontWeight: 600,
            fontSize: '15px',
            lineHeight: '20px',
            color: colors.OFF_BLACK,
          }}>
          {text}
        </Typography>
      </Box>
    );
  }

  const eligibilityNote = GIFT_CAMPAIGN.copy.eligibilityNoteTemplate.replace(
    '{{plans}}',
    joinPlanLabels(eligiblePlanLabels)
  );

  return (
    <Box mt={'16px'} mx={'8px'} position={'relative'} pt={'22px'}>
      {/* Badge — overlaps top edge of card */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '16px',
          right: '16px',
          height: '44px',
          px: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.WHITE,
          border: `1px solid ${colors.SALAD}`,
          borderRadius: '16px',
          zIndex: 1,
        }}>
        <Typography
          sx={{
            fontFamily: 'Lexend, sans-serif',
            fontWeight: 600,
            fontSize: '15px',
            lineHeight: '20px',
            textAlign: 'center',
            color: colors.OFF_BLACK,
          }}>
          {`\u{1F381} ${GIFT_CAMPAIGN.copy.cardBadge}`}
        </Typography>
      </Box>

      {/* Card body */}
      <Box
        bgcolor={colors.OFF_WHITE}
        borderRadius={'20px'}
        pt={'48px'}
        pb={'32px'}
        px={'16px'}>
        <Stack spacing={'16px'} width={'100%'} alignItems={'stretch'}>
          <Typography
            textAlign={'center'}
            sx={{
              fontFamily: 'Lexend, sans-serif',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
              color: colors.SALAD,
            }}>
            {GIFT_CAMPAIGN.copy.cardTitle}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}>
            <Box
              component={'img'}
              src={supplementsPackImage}
              alt={'Free protein powder gift'}
              sx={{
                width: '161px',
                height: '90px',
                objectFit: 'cover',
                borderRadius: '20px',
              }}
            />
          </Box>

          <Stack spacing={'12px'} alignItems={'stretch'}>
            {GIFT_CAMPAIGN.copy.benefits.map(b => (
              <GiftBenefitRow key={b.title} benefit={b} />
            ))}
            <GiftBenefitRow benefit={{ title: eligibilityNote }} />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default GiftEligibilityCard;
